import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useStationsContext } from '../../context/StationsContext';

const HEADER_MAP = {
    // Common
    time: "Time",
    date: "Date",
    stationName: "Station",

    // Daily
    at2mDegC: "Temp (2m) °C",
    at4mDegC: "Temp (4m) °C",
    at6mDegC: "Temp (6m) °C",
    rh2mPer: "Humidity (%)",
    ws2mMps: "Wind Speed (m/s)",
    ws10mMps: "Wind Speed (10m) m/s",
    wd2mDeg: "Wind Dir (Deg)",
    rainFallMm: "Rainfall (mm)",

    // Yearly averages might have similar keys or prefixed
    avgTemp: "Avg Temp",
    totalRain: "Total Rain"
};

const formatValue = (key, value, getStationName) => {
    if (key === 'stationName' && typeof value === 'string') {
        return getStationName ? getStationName(value) : value;
    }
    if (typeof value === 'number') {
        // Round to 2 decimals for floats
        return Number.isInteger(value) ? value : value.toFixed(2);
    }
    return value;
};

const WeatherTable = ({ data, loading }) => {
    const { getStationName } = useStationsContext();
    if (loading) return <div className="glass-panel" style={{ padding: '1rem' }}>Loading table...</div>;
    if (!data || data.length === 0) return <div className="glass-panel" style={{ padding: '1rem' }}>No data available</div>;

    const headers = Object.keys(data[0]);

    return (
        <div className="glass-panel" style={{ padding: '0', display: 'flex', flexDirection: 'column', minHeight: '300px', maxHeight: '600px', overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.5rem', background: 'var(--primary-main)', }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'white' }}>Data Table ({data.length} records)</h3>
            </div>

            <TableContainer
                component={Paper}
                sx={{
                    background: 'transparent',
                    boxShadow: 'none',
                    flex: 1,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '3px' }
                }}
            >
                <Table stickyHeader size="small" aria-label="weather table">
                    <TableHead>
                        <TableRow>
                            {headers.map((head) => (
                                <TableCell
                                    key={head}
                                    sx={{
                                        background: 'var(--bg-default)',
                                        color: 'var(--text-secondary)',
                                        borderBottom: '2px solid rgba(0,0,0,0.05)',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        textTransform: 'uppercase',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {HEADER_MAP[head] || head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                    transition: 'background-color 0.2s',
                                    '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.01)' }
                                }}
                            >
                                {headers.map((key) => (
                                    <TableCell key={key} sx={{ color: 'var(--text-primary)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        {formatValue(key, row[key], getStationName)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default WeatherTable;
